import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Shield,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCcw,
  Download,
  ShieldCheck,
  FileText,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  getChainAnchors,
  getChainAnchor,
  verifyChainAnchor,
  generateChainAnchor,
} from '../../services/api'
import StatCard from '../../components/StatCard'

function shortHash(hex) {
  if (!hex) return '—'
  return `${hex.slice(0, 8)}…${hex.slice(-6)}`
}

function firstOfPrevMonth() {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().split('T')[0]
}

export default function EvidenceLedger({ ranchId }) {
  const queryClient = useQueryClient()
  const [targetMonth, setTargetMonth] = useState(firstOfPrevMonth())
  const [generateError, setGenerateError] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  const { data: anchors, isLoading } = useQuery({
    queryKey: ['chainAnchors', ranchId],
    queryFn: () => getChainAnchors(ranchId),
    enabled: Boolean(ranchId),
  })

  const generateMutation = useMutation({
    mutationFn: (month) => generateChainAnchor(ranchId, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chainAnchors'] })
      setGenerateError(null)
    },
    onError: (err) => setGenerateError(err.response?.data?.detail || 'Failed to generate manifest'),
  })

  const count = anchors?.length || 0
  const anchored = anchors?.filter((a) => a.anchor_status === 'anchored').length || 0
  const latest = anchors?.[0]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/reports" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-6 w-6 text-purple-600" /> Evidence Ledger
            </h1>
            <p className="text-gray-500 mt-1">
              Monthly MRV evidence manifests — canonicalised JSON bundles hashed with SHA-256.
              Each row below is a tamper-evident record of that month's audit artefacts.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Manifests Recorded"
          value={count}
          icon={FileText}
        />
        <StatCard
          title="On-Chain Anchored"
          value={anchored}
          icon={ShieldCheck}
        />
        <StatCard
          title="Latest Hash"
          value={latest ? shortHash(latest.manifest_sha256) : '—'}
          icon={Shield}
        />
      </div>

      <div className="card p-4">
        <h2 className="text-sm font-medium text-gray-700 mb-2">Generate Manifest</h2>
        <p className="text-xs text-gray-500 mb-3">
          Any day in the target calendar month. Omit to use the previous full month.
          Identical content produces the same hash and does not create a new version.
        </p>
        <div className="flex gap-2 items-end">
          <div>
            <label className="label">Target month</label>
            <input
              type="date"
              className="input"
              value={targetMonth}
              onChange={(e) => setTargetMonth(e.target.value)}
            />
          </div>
          <button
            onClick={() => generateMutation.mutate(targetMonth)}
            disabled={generateMutation.isPending || !ranchId}
            className="btn bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1"
          >
            {generateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            Build + Hash
          </button>
          {generateMutation.data && (
            <span
              className={`text-sm ${
                generateMutation.data.unchanged ? 'text-gray-500' : 'text-green-700'
              }`}
            >
              {generateMutation.data.unchanged
                ? `Unchanged (v${generateMutation.data.manifest_version})`
                : `Recorded v${generateMutation.data.manifest_version} · ${shortHash(
                    generateMutation.data.manifest_sha256
                  )}`}
            </span>
          )}
        </div>
        {generateError && (
          <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">{generateError}</div>
        )}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : count === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No evidence manifests recorded yet. Use the form above to build one.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Month
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Version
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  SHA-256
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Records
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Photos
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Size
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Computed
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {anchors.map((a) => (
                <AnchorRow
                  key={a.id}
                  anchor={a}
                  expanded={expandedId === a.id}
                  onToggle={() => setExpandedId(expandedId === a.id ? null : a.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function recordCount(a) {
  return (
    Number(a.transition_summary_count || 0) +
    Number(a.drone_run_count || 0) +
    Number(a.vegetation_polygon_count || 0) +
    Number(a.spot_check_count || 0) +
    Number(a.soil_sample_count || 0) +
    Number(a.biomass_sample_count || 0) +
    Number(a.biochar_audit_count || 0)
  )
}

function AnchorRow({ anchor, expanded, onToggle }) {
  const [verifyResult, setVerifyResult] = useState(null)

  const verifyMutation = useMutation({
    mutationFn: () => verifyChainAnchor(anchor.id),
    onSuccess: (data) => setVerifyResult(data),
    onError: (err) => setVerifyResult({ valid: false, error: err.response?.data?.detail }),
  })

  const detailQuery = useQuery({
    queryKey: ['chainAnchor', anchor.id],
    queryFn: () => getChainAnchor(anchor.id),
    enabled: expanded,
  })

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm">
          {anchor.manifest_month
            ? format(parseISO(anchor.manifest_month), 'MMMM yyyy')
            : '—'}
        </td>
        <td className="px-4 py-3 text-sm font-mono">v{anchor.manifest_version}</td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700" title={anchor.manifest_sha256}>
          {shortHash(anchor.manifest_sha256)}
        </td>
        <td className="px-4 py-3 text-sm">{recordCount(anchor)}</td>
        <td className="px-4 py-3 text-sm">{anchor.photo_count || 0}</td>
        <td className="px-4 py-3 text-sm font-mono text-gray-500">
          {anchor.manifest_byte_size ? `${(anchor.manifest_byte_size / 1024).toFixed(1)} KB` : '—'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-500">
          {anchor.computed_at
            ? format(parseISO(anchor.computed_at), 'dd MMM yyyy HH:mm')
            : '—'}
        </td>
        <td className="px-4 py-3 text-sm">
          <div className="flex gap-2 items-center">
            <button
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1"
            >
              {verifyMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : verifyResult ? (
                verifyResult.valid ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )
              ) : (
                <Shield className="h-4 w-4" />
              )}
              Verify
            </button>
            <a
              href={`/api/v1/chain-anchors/${anchor.id}/manifest`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center gap-1"
            >
              <Download className="h-4 w-4" /> JSON
            </a>
            <button
              onClick={onToggle}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium"
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={8} className="px-4 py-3">
            {detailQuery.isLoading ? (
              <div className="text-sm text-gray-500">Loading manifest…</div>
            ) : detailQuery.data ? (
              <ManifestPreview data={detailQuery.data} verify={verifyResult} />
            ) : (
              <div className="text-sm text-red-500">Failed to load manifest</div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function ManifestPreview({ data, verify }) {
  const counts = {
    transition_summaries: data.transition_summary_count,
    drone_runs: data.drone_run_count,
    vegetation_polygons: data.vegetation_polygon_count,
    spot_checks: data.spot_check_count,
    soil_samples: data.soil_sample_count,
    biomass_samples: data.biomass_sample_count,
    biochar_audits: data.biochar_audit_count,
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase text-gray-500">SHA-256</div>
          <div className="font-mono text-xs break-all" title={data.manifest_sha256}>
            {data.manifest_sha256}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase text-gray-500">Schema</div>
          <div>{data.schema_version}</div>
        </div>
        <div>
          <div className="text-xs uppercase text-gray-500">Anchor Status</div>
          <div>
            {data.anchor_status}
            {data.anchor_chain ? ` (${data.anchor_chain})` : ''}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase text-gray-500">Byte Size</div>
          <div className="font-mono">{data.manifest_byte_size || '—'}</div>
        </div>
      </div>

      {verify && (
        <div
          className={`text-sm p-2 rounded flex items-center gap-2 ${
            verify.valid
              ? 'bg-green-50 text-green-800'
              : 'bg-red-50 text-red-800'
          }`}
        >
          {verify.valid ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {verify.valid
            ? `Hash verified — recomputed SHA-256 matches the stored digest (${shortHash(
                verify.recomputed_sha256
              )}).`
            : `Hash MISMATCH — stored ${shortHash(verify.stored_sha256)} vs recomputed ${shortHash(
                verify.recomputed_sha256
              )}. The canonical bytes have been altered.`}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="bg-white border border-gray-200 rounded p-2">
            <div className="text-gray-500 uppercase">{k.replace(/_/g, ' ')}</div>
            <div className="text-lg font-semibold text-gray-900">{v || 0}</div>
          </div>
        ))}
      </div>

      {data.manifest?.source_tables && (
        <div className="text-xs text-gray-500">
          <span className="font-medium">Source tables:</span>{' '}
          {data.manifest.source_tables.join(', ')}
        </div>
      )}
    </div>
  )
}
